/**
 * Werehouses Receipt
 */

// React
import React, { FunctionComponent, useEffect, useState, useContext } from 'react';

// Context
import AuthContext from '../../authContext';

// Components
import Pagination from '@material-ui/lab/Pagination';
import List from './components/list';

import RenderFilter from '../../utils/listDaynamicFilterHookForm';

// UI
import theme from '../../theme';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  DialogTitle,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Box,
  TextField,
  Card,
  CardHeader,
  CardContent,
  Divider,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiCheck,
  mdiClose,
  mdiCalendar,
  mdiMapMarkerOutline,
  mdiKeyOutline,
  mdiStateMachine,
  mdiPencilOutline,
} from '@mdi/js';
import {
  CreatedIcon,
  AssignedIcon,
  EvacuatedIcon,
  RegisteredIcon,
} from './iconCollection';

// Helper
import {
  removeEmptyValueObject,
  translateParcelsStatus,
} from '../../utils/helpers';
import useQuery from '../../utils/hook/useQuery';

// Type
import { Props, Status } from './interfaces';
import type { Filter } from '../../utils/listDaynamicFilter';
import handleError from '../../utils/handleError';
import handleNotfication from '../../utils/handleNotfication';
import parcelsRepository from 'client-sdk/src/Repository/parcels-repository';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '30px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },

    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },

    boxshadow: {
      position: 'relative',
      backgroundColor: 'white',
      padding: '14px',
      transform: 'translateY(-3px)',
      boxShadow: '-6px 6px 14px 0px rgba(0,0,0,0.15) !important',
    },
    cards: {
      position: 'relative',
      backgroundColor: 'white',
      padding: '14px',
    },
    iconHeader: {
      position: 'absolute',
      top: '5px',
      right: '5px',
    },
    cardContent: {
      marginTop: '-22px',
      display: 'flex',
    },
    cardContentlist: {
      justifyContent: 'space-between',
      padding: ' 0',
    },
    mobile: {
      maxWidth: '120%',
      margin: '16px 0',
      '@media (max-width: 480px)': {
        marginTop: '140px',
      },
    },
  })
);

const Parcels: FunctionComponent<Props> = (props: Props) => {
  const authContext = useContext(AuthContext);
  const [rejectDescription, setRejectDescription] = useState('');
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 10,
  } as any);
  const [filters, setFilters] = useState([] as Filter[]);
  const [openConfirmDisable, setOpenConfirmDisable] = useState(
    false as boolean
  );

  const [id, setId] = useState(-1 as number);
  const [openConfirmActive, setOpenConfirmActive] = useState(false as boolean);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);

  const [parcelsList, setParcelsList] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardCounts, setCardCounts] = useState({});

  const checkActiveCard = (name: string) => {
    const status = filtersValues['status.in']?.split(',') || [];
    switch (name) {
      case 'created':
        return status.includes('SUBMITTED');
      case 'assigned':
        return status.includes('ASSIGNED');
      case 'unloaded':
        return status.includes('UNLOADED');
      case 'registered':
        return status.includes('REGISTERED');
    }
  };

  const handleCardClick = (name: any) => {
    switch (name) {
      case 'created':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['SUBMITTED'],

          page: 0,
          size: 10,
        });
        break;
      case 'assigned':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['ASSIGNED'],

          page: 0,
          size: 10,
        });
        break;
      case 'unloaded':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['UNLOADED'],
          page: 0,
          size: 10,
        });
        break;
      case 'registered':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['REGISTERED'],
          page: 0,
          size: 10,
        });
        break;
    }
  };

  const handleCardClose = () => {
    delete filtersValues['status.in'];
    setFiltersValues({
      ...removeEmptyValueObject(filtersValues),

      page: 0,
      size: 10,
    });
  };
  const getingCounts = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const submitted = await parcelsRepository.getParcelsCount({
        ...payload.submitted,
      });
      const assigned = await parcelsRepository.getParcelsCount({
        ...payload.assigned,
      });
      const unloaded = await parcelsRepository.getParcelsCount({
        ...payload.unloaded,
      });
      const registered = await parcelsRepository.getParcelsCount({
        ...payload.registered,
      });
      setCardCounts({ submitted, assigned, unloaded, registered });
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const getCounts = () => {
    const { warehouseId } = authContext.user;

    const params = warehouseId
      ? { destinationWarehouseId: { equals: warehouseId } }
      : {};
    getingCounts({
      submitted: { 'status.in': ['SUBMITTED'], ...params },
      assigned: { 'status.in': ['ASSIGNED'], ...params },
      unloaded: { 'status.in': ['UNLOADED'], ...params },
      registered: { 'status.in': ['REGISTERED'], ...params },
    });
  };
  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش‌دهنده:',
      'customerName.equals': 'نام سفارش‌دهنده:',
      'customerName.contais': 'نام سفارش‌دهنده:',
      centerName: 'انبار:',
      'centerName.equals': 'انبار:',
      'centerName.contains': 'انبار:',
      id: 'شناسه مرسوله:',
      'id.equals': 'شناسه مرسوله:',
      'id.contains': 'شناسه مرسوله:',
      orderId: 'شناسه سفارش:',
      'orderId.equals': 'شناسه سفارش:',
      'orderId.contains': 'شناسه سفارش:',
      deliveryDate: 'تاریخ تحویل',
      'deliveryDate.equals': 'تاریخ تحویل:',
      'deliveryDate.greaterThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.greaterThan': 'تاریخ تحویل:',
      'deliveryDate.lessThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.lessThan': 'تاریخ تحویل:',
      packagedDate: 'تاریخ تحویل به لجستیک:',
      'packagedDate.equals': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThan': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThan': 'تاریخ تحویل به لجستیک:',
      status: 'وضعیت سفارش:',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
      partnerName: 'شرکت:',
      'partnerName.equals': 'شرکت:',
      'partnerName.contains': 'شرکت:',
      weight: 'وزن:',
      'weight.equals': 'وزن:',
      'weight.contains': 'وزن:',
      length: 'ابعاد:',
      'length.equals': 'ابعاد:',
      'length.contains': 'ابعاد:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };
  useEffect(() => {
    getParcels();
    getCounts();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps
  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };
  const getParcel = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response, count } = await parcelsRepository.getParcels({ payload });
      setParcelsList(response.data);
      setCount(count.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const getParcels = () => {
    const { page, size } = filtersValues;
    const { warehouseId } = authContext.user;

    const params = warehouseId
      ? { destinationWarehouseId: { equals: warehouseId } }
      : {};

    getParcel({
      ...removeEmptyValueObject(filtersValues),
      ...params, // WHID
      page,
      size,
    });
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };

  const handleOpen = (id: number, type: string) => {
    setId(id);
  };

  const closeConfirmUpdateState = () => {
    setOpenConfirmUpdateState(false);
  };

  const handleClose = () => {
    setOpenConfirmDisable(false);
    setOpenConfirmActive(false);
    setOpenSize(false);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: any }) => {
    setId(data.id);
    setOpenConfirmUpdateState(true);
    setPreviewState(data.body);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'sourceName.contains',
        label: 'مبدا',
        icon: mdiMapMarkerOutline,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه محموله ',
        icon: mdiKeyOutline,
      },
      {
        type: 'checkbox',
        id: 'status.in',
        label: 'وضعیت محموله',
        data: [
          'SUBMITTED',
          'SHIPPED',
          'DELIVERED',
          'ASSIGNED',
          'UNLOADING',
          'UNLOADED',
          'REGISTERED',
          'LOCATED',
          'CONFLICTED',
          'RETURNING',
          'CANCELLED',
        ],
        translateType: 'parcel',
        icon: mdiStateMachine,
      },
      {
        type: 'date-picker',
        id: 'shippingDate.greaterThanOrEqual',
        label: 'تاریخ ثبت محموله از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.lessThanOrEqual',
        label: 'تا تاریخ ثبت محموله ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.greaterThanOrEqual',
        label: 'تاریخ تحویل از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.lessThanOrEqual',
        label: 'تا تاریخ تحویل',
        icon: mdiCalendar,
      },
    ]);
  };
  const headCells = [
    {
      id: 'sourceName',
      label: 'مبدا',
      type: 'input',
    },
    {
      id: 'id',
      label: 'شناسه محموله',
      type: 'input',
    },
    {
      id: 'shippingDate',
      label: 'تاریخ ارسال',
      type: 'date-picker',
    },
    {
      id: 'deliveryDate',
      label: 'تاریخ تحویل',
      type: 'date-picker',
    },
    {
      id: 'itemsCount',
      label: 'تعداد تنوع',
      type: 'input',
    },
    {
      id: 'inventoryItemsCount',
      label: 'تعداد آیتم',
      type: 'input',
    },
    {
      id: 'status',
      label: 'وضعیت',
      type: 'checkbox',
      data: [
        'SUBMITTED',
        'SHIPPED',
        'DELIVERED',
        'ASSIGNED',
        'UNLOADING',
        'UNLOADED',
        'REGISTERED',
        'LOCATED',
        'CONFLICTED',
        'RETURNING',
        'CANCELLED',
      ],
      translateType: 'ParcelsStatus',
    },
  ];
  const head = { headCells };
  const updateParcelsStatus = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const callback = await parcelsRepository.updateParcelStatus({ payload });
      handleNotfication('محموله با موفقیت به روز رسانی شد', 'success');
      getParcels();
      getCounts();
      callback && callback();
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    const callback = () => {
      getParcels();
      getCounts();
    };
    await updateParcelsStatus({
      id,
      body: { status: 'CANCELLED'.replace(/'/g, '"'), description: '' },
      pagination: filtersValues,
      callback,
    });
    setOpenConfirmDisable(false);
  };

  const handleActive = async () => {
    const callback = () => {
      getParcels();
      getCounts();
    };
    await updateParcelsStatus({
      id,
      body: { status: 'ACTIVE'.replace(/'/g, '"'), description: '' },
      pagination: filtersValues,
      callback,
    });

    setOpenConfirmActive(false);
  };

  const handleOpenConfirmDisable = (id: number) => {
    setOpenConfirmDisable(true);
    setId(id);
  };
  const handleOpenConfirmActive = (id: number) => {
    setOpenConfirmActive(true);
    setId(id);
  };

  const changeStatusParcel = () => {
    const { warehouseId } = authContext.user;
    const params = warehouseId
      ? {
        destinationWarehouseId: { equals: warehouseId },
      }
      : { ...filtersValues };

    const desc = previewState === 'RETURNING' ? rejectDescription : '';

    updateParcelsStatus({
      id,
      body: { status: previewState, description: desc },
      pagination: params,
    });
    setOpenConfirmUpdateState(false);
  };

  const findStatus = (id: number) => {
    const status = parcelsList.find(parcel => parcel.id === id)
      ?.status as Status;
    return status && translateParcelsStatus(status);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={6}>
        <Grid container item xs={12} sm={6} alignItems="center">
          <Typography variant="h4" component="h2">
            {'  محموله‌های دریافتی  '}
          </Typography>
        </Grid>

        <Grid item sm={6} xs={12}>
          <RenderFilter
            filters={filters}
            onFilter={(filtervalues: any) => {
              setFiltersValues({
                ...removeEmptyValueObject(filtervalues),
                page: 0,
                size: 10,
              });
            }}
            mainFiltersCount={0}
            baseFilter={{
              type: 'input',
              id: 'id.equals',
              placeholder: 'شناسه محموله',
            }}
            filterValues={filtersValues}
          />
        </Grid>
      </Grid>

      <Box mb={5} />
      <Grid container spacing={2} md={12} className={classes.mobile}>
        <Grid container lg={12} spacing={2} xs={12}>
          <Grid item md={6} lg={3} xs={12}>
            <Card
              className={
                checkActiveCard('created') ? classes.boxshadow : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('created') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                      data-cy="cardCloseBtn"
                    >
                      <Icon
                        path={mdiClose}
                        size={1}
                        style={{
                          color: '#Ffffff',
                          backgroundColor: '#FFA65A',
                          borderRadius: '3px',
                        }}
                      />
                    </IconButton>
                  )
                }
              />
              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  onClick={name => handleCardClick('created')}
                  aria-label="search"
                  style={{
                    backgroundColor: '#DFE3FD',
                    height: '30%',
                    width: '28%',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                  data-cy="progressCardBtn"
                >
                  <CreatedIcon />
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                    data-cy="processingCount"
                  >
                    {cardCounts?.submitted?.data}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    ایجاد شده
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            <Card
              className={
                checkActiveCard('assigned') ? classes.boxshadow : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('assigned') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                      data-cy="cardCloseBtn"
                    >
                      <Icon
                        path={mdiClose}
                        size={1}
                        style={{
                          color: '#Ffffff',
                          backgroundColor: '#FFA65A',
                          borderRadius: '3px',
                        }}
                      />
                    </IconButton>
                  )
                }
              />
              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  aria-label="search"
                  onClick={name => handleCardClick('assigned')}
                  style={{
                    backgroundColor: '#FCEFDB',
                    height: '60%',
                    width: '30%',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                  data-cy="courierCardBtn"
                >
                  <AssignedIcon />
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                    data-cy="deliveringCount"
                  >
                    {cardCounts?.assigned?.data}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    تخصیص شده
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            <Card
              className={
                checkActiveCard('unloaded') ? classes.boxshadow : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('unloaded') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                      data-cy="cardCloseBtn"
                    >
                      <Icon
                        path={mdiClose}
                        size={1}
                        style={{
                          color: '#Ffffff',
                          backgroundColor: '#FFA65A',
                          borderRadius: '3px',
                        }}
                      />
                    </IconButton>
                  )
                }
              />

              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  aria-label="search"
                  onClick={name => handleCardClick('unloaded')}
                  style={{
                    backgroundColor: '#D0ECF5',
                    height: '50%',
                    width: '30%',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                  data-cy="postCardBtn"
                >
                  <EvacuatedIcon />
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                    data-cy="deliverdPostCount"
                  >
                    {cardCounts?.unloaded?.data}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    تخلیه شده
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            <Card
              className={
                checkActiveCard('registered')
                  ? classes.boxshadow
                  : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('registered') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                      data-cy="cardCloseBtn"
                    >
                      <Icon
                        path={mdiClose}
                        size={1}
                        style={{
                          color: '#Ffffff',
                          backgroundColor: '#FFA65A',
                          borderRadius: '3px',
                        }}
                      />
                    </IconButton>
                  )
                }
              />
              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  aria-label="search"
                  onClick={name => handleCardClick('registered')}
                  style={{
                    backgroundColor: '#D6EBD3',
                    height: '50%',
                    width: '30%',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                  data-cy="deliveredCardBtn"
                >
                  <RegisteredIcon />
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                    data-cy="deliveredCount"
                  >
                    {cardCounts?.registered?.data}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    رجیستر شده
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Box mb={5} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {parcelsList.length > 0 ? (
                <List
                  data={parcelsList}
                  handleOpenConfirmDisable={handleOpenConfirmDisable}
                  handleOpenConfirmActive={handleOpenConfirmActive}
                  handleOpen={handleOpen}
                  changeStatus={handleOpenConfirmUpdateState}
                  head={head}
                  filterVal={filtersValues}
                  onFilterValue={(filtervalues: any) => {
                    setFiltersValues({
                      ...removeEmptyValueObject(filtervalues),
                      ...filtersValues,
                      page: 0,
                      size: 10,
                    });
                  }}
                  removeFilterHandler={removeFilterHandler}
                  filterLabelTranslator={filterTranslator}
                />
              ) : (
                <Typography variant="h3" component="div">
                  محموله موجود نیست
                </Typography>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {parcelsList.length > 0 && (
            <FormControl
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                {' '}
                تعداد نمایش در هر صفحه:
              </div>
              <Select
                open={openSize}
                onClose={handleClose}
                onOpen={() => setOpenSize(true)}
                value={filtersValues.size}
                onChange={handleChangeSizePage}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid xs={12} sm={6} container item direction="row-reverse">
          {parcelsList.length > 0 && (
            <Pagination
              className={classes.pagination}
              count={Math.ceil(count / filtersValues.size)}
              color="secondary"
              page={filtersValues.page + 1}
              onChange={handleChangePage}
            />
          )}
        </Grid>
      </Grid>

      <Dialog
        fullWidth
        open={openConfirmDisable || openConfirmActive}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {handleClose ? (
              <IconButton
                aria-label="close"
                // className={classes.closeButton}
                onClick={handleClose}
              >
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div
            className={
              openConfirmDisable ? classes.cancelIcon : classes.editIcon
            }
          >
            {openConfirmDisable ? (
              <Icon path={mdiClose} size={4} color={theme.palette.error.main} />
            ) : (
              <Icon path={mdiCheck} size={4} color={theme.palette.blue.main} />
            )}
          </div>
          <div className={classes.cancelTitle}>
            <div className={classes.cancelTitle}>
              {` از 
            
            لغو محموله با شناسه
          "${parcelsList.find(item => item.id === id)?.id}"
          مطمئن هستید؟`}
            </div>
          </div>
        </DialogTitle>
        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={handleClose}
                variant="contained"
                color="secondary"
                size="large"
              >
                {'خیر'}
              </Button>
            </Grid>

            <Grid item xs={4}>
              {openConfirmDisable ? (
                <Button
                  onClick={handleDisable}
                  fullWidth
                  variant="contained"
                  size="large"
                >
                  {'بله'}
                </Button>
              ) : (
                <Button
                  onClick={handleActive}
                  fullWidth
                  variant="contained"
                  size="large"
                >
                  {'بله'}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        open={openConfirmUpdateState}
        onClose={closeConfirmUpdateState}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogContent>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {closeConfirmUpdateState ? (
              <IconButton
                aria-label="close"
                // className={classes.closeButton}
                onClick={closeConfirmUpdateState}
              >
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div className={classes.editIcon}>
            <Icon
              path={mdiPencilOutline}
              size={4}
              color={theme.palette.blue.main}
            />
          </div>
          <div className={classes.cancelTitle}>
            {`از تغییر تگ وضعیت محموله
                با شناسه  ${id} از
 "${id > -1 && findStatus(id)}"
 به
" ${translateParcelsStatus(previewState.replace(/"/g, '') as Status)}"
 مطمئن هستید؟`}
          </div>
          {previewState === 'RETURNING' ? (
            <TextField
              className={classes.textArea}
              placeholder={`علت ریجکت...`}
              value={rejectDescription}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRejectDescription(e.target.value)
              }
            />
          ) : null}
        </DialogContent>
        <Divider />
        <DialogActions className={classes.actionsBox}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={2}>
              <Button
                fullWidth
                autoFocus
                onClick={closeConfirmUpdateState}
                variant="outlined"
                size="large"
              >
                {'انصراف'}
              </Button>
            </Grid>
            <Grid item xs={5}>
              <Button
                fullWidth
                variant="contained"
                onClick={changeStatusParcel}
                size="large"
                color="primary"
              >
                {'تغییر وضعیت محموله'}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
//   parcelsList: makeSelectList(),
//   count: makeSelectCountList(),
//   loading: makeSelectLoading(),
//   error: makeSelectError(),
// });

// function mapDispatchToProps(dispatch: Function) {
//   return {
//     parcelsDispatch: (payload: Record<string, any>) =>
//       dispatch(getParcelsAction(payload)),
//     updateParcelDispatch: (payload: PayloadUpdate) =>
//       dispatch(updateParcelsActions(payload)),
//     createWarehouseDispatch: (payload: PayloadCreate) =>
//       dispatch(createParcelsActions(payload)),
//     updateParcelsStatusDispatch: (payload: {
//       id: number;
//       body: object;
//       pagination: object;
//     }) => dispatch(updateParcelsStatusActions(payload)),
//   };
// }

// const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default Parcels;
